import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, TextField, Button, Paper, Typography, CircularProgress,
  List, ListItem, ListItemText, Divider, Grid, Collapse, ToggleButton,
  ToggleButtonGroup, Pagination, Accordion, AccordionSummary, AccordionDetails,
  Checkbox, Chip, FormControl, InputLabel, Select, MenuItem, Autocomplete
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { searchConstitution } from '../services/api';
import PageSizeSelector, { DEFAULT_PAGE_SIZE } from './PageSizeSelector';
import { sortFacetsBooleansFirst } from '../utils/facetOrder';

export default function ConstitutionSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchMode, setSearchMode] = useState(searchParams.get('mode') || 'simple');
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedDetails, setExpandedDetails] = useState({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    const urlPageSize = parseInt(searchParams.get('pageSize'), 10);
    return [10, 25, 50, 100].includes(urlPageSize) ? urlPageSize : DEFAULT_PAGE_SIZE;
  });
  const [activeFilters, setActiveFilters] = useState({});
  const [facets, setFacets] = useState({});

  // Advanced form state
  const [form, setForm] = useState({
    keywords: '',
    chapter: '',
    provisionType: '',
    sectionNumber: '',
    theme: ''
  });

  // Options for dropdown/autocomplete fields
  const [availableOptions, setAvailableOptions] = useState({
    chapters: [],
    provisionTypes: [],
    themes: []
  });

  // Fetch options for dropdowns on mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const data = await searchConstitution('*', 1, 100);
        if (data.facets) {
          const chapters = data.facets.chapters?.map(f => f.value) || [];
          const provisionTypes = data.facets.provision_types?.map(f => f.value) || [];
          const themes = data.facets.themes?.map(f => f.value) || [];
          setAvailableOptions({ chapters, provisionTypes, themes });
        }
      } catch (error) {
        console.error('Failed to fetch options:', error);
      }
    };
    fetchOptions();
  }, []);

  useEffect(() => {
    const urlQuery = searchParams.get('q');
    if (urlQuery) {
      handleSearch(urlQuery);
    } else {
      handleSearch('*');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildAdvancedQuery = () => {
    const parts = [];

    if (form.keywords) {
      parts.push(`content:${form.keywords}`);
    }
    if (form.chapter) {
      parts.push(`chapter:"${form.chapter}"`);
    }
    if (form.provisionType) {
      parts.push(`provision_type:"${form.provisionType}"`);
    }
    if (form.sectionNumber) {
      parts.push(`section_number:"${form.sectionNumber}"`);
    }
    if (form.theme) {
      parts.push(`themes:"${form.theme}"`);
    }

    return parts.length > 0 ? parts.join(' AND ') : '*';
  };

  const buildQueryWithFilters = (baseQuery, filters) => {
    const filterParts = [];
    Object.entries(filters).forEach(([field, values]) => {
      if (values.length > 0) {
        const fieldQuery = values.map(v => `${field}:"${v}"`).join(' OR ');
        filterParts.push(`(${fieldQuery})`);
      }
    });

    if (filterParts.length === 0) return baseQuery;
    if (baseQuery === '*') return filterParts.join(' AND ');
    return `(${baseQuery}) AND ${filterParts.join(' AND ')}`;
  };

  const handleSearch = async (searchQuery = null, newPage = 1, filters = activeFilters, pageSize = perPage) => {
    let baseQuery;
    if (searchMode === 'simple') {
      baseQuery = searchQuery !== null ? searchQuery : (query || '*');
    } else {
      baseQuery = buildAdvancedQuery();
    }

    const q = buildQueryWithFilters(baseQuery, filters);

    setLoading(true);
    try {
      const data = await searchConstitution(q, newPage, pageSize);
      setResults(data);
      setPage(newPage);
      if (data.facets) {
        setFacets(data.facets);
      }
      const newParams = { q: baseQuery, mode: searchMode };
      if (pageSize !== DEFAULT_PAGE_SIZE) newParams.pageSize = pageSize;
      setSearchParams(newParams);
    } catch (error) {
      console.error('Search error:', error);
    }
    setLoading(false);
  };

  const handleFacetClick = (field, value) => {
    const newFilters = { ...activeFilters };
    if (!newFilters[field]) newFilters[field] = [];
    const valueIndex = newFilters[field].indexOf(value);
    if (valueIndex > -1) {
      newFilters[field] = newFilters[field].filter(v => v !== value);
      if (newFilters[field].length === 0) delete newFilters[field];
    } else {
      newFilters[field].push(value);
    }
    setActiveFilters(newFilters);
    handleSearch(null, 1, newFilters);
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    handleSearch(null, 1, {});
  };

  const facetToField = (facetName) => {
    const mapping = {
      'chapters': 'chapter',
      'provision_types': 'provision_type',
      'amendment_statuses': 'amendment_status',
      'themes': 'themes'
    };
    return mapping[facetName] || facetName;
  };

  const facetDisplayName = (facetName) => {
    const names = {
      'chapters': 'Chapter',
      'provision_types': 'Provision Type',
      'amendment_statuses': 'Amendment Status',
      'themes': 'Theme'
    };
    return names[facetName] || facetName;
  };

  const handlePageChange = (event, newPage) => {
    handleSearch(null, newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPerPage(newSize);
    handleSearch(null, 1, activeFilters, newSize);
  };

  const handleReset = () => {
    setQuery('');
    setForm({
      keywords: '',
      chapter: '',
      provisionType: '',
      sectionNumber: '',
      theme: ''
    });
    setResults(null);
    setPage(1);
    setActiveFilters({});
    setFacets({});
    setSearchParams({});
  };

  const toggleDetails = (id) => {
    setExpandedDetails(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getAmendmentStatusColor = (status) => {
    switch (status) {
      case 'amended': return 'warning';
      case 'repealed': return 'error';
      case 'original': return 'success';
      default: return 'default';
    }
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setSearchMode(newMode);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Australian Constitution</Typography>
        <ToggleButtonGroup
          value={searchMode}
          exclusive
          onChange={handleModeChange}
          size="small"
        >
          <ToggleButton value="simple">
            <SearchIcon sx={{ mr: 1 }} /> Simple
          </ToggleButton>
          <ToggleButton value="advanced">
            <TuneIcon sx={{ mr: 1 }} /> Advanced
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Simple Search */}
      <Collapse in={searchMode === 'simple'}>
        <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              placeholder='Try "parliament" or "executive power" or section:51'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
            <Button
              variant="contained"
              onClick={() => handleSearch()}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ borderRadius: 3, px: 4 }}
            >
              Search
            </Button>
            <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 3 }}>
              Reset
            </Button>
          </Box>
        </Paper>
      </Collapse>

      {/* Advanced Search */}
      <Collapse in={searchMode === 'advanced'}>
        <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Keywords"
                value={form.keywords}
                onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                placeholder="Search across all content"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Chapter</InputLabel>
                <Select
                  value={form.chapter}
                  onChange={(e) => setForm({ ...form, chapter: e.target.value })}
                  label="Chapter"
                >
                  <MenuItem value="">Any</MenuItem>
                  {availableOptions.chapters.map(chapter => (
                    <MenuItem key={chapter} value={chapter}>Chapter {chapter}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Provision Type</InputLabel>
                <Select
                  value={form.provisionType}
                  onChange={(e) => setForm({ ...form, provisionType: e.target.value })}
                  label="Provision Type"
                >
                  <MenuItem value="">Any</MenuItem>
                  {availableOptions.provisionTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Section Number"
                placeholder='e.g., "51", "128"'
                value={form.sectionNumber}
                onChange={(e) => setForm({ ...form, sectionNumber: e.target.value })}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.themes}
                value={form.theme}
                onChange={(e, v) => setForm({ ...form, theme: v || '' })}
                renderInput={(params) => <TextField {...params} label="Theme" placeholder="e.g., taxation, defence" />}
                freeSolo
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  onClick={() => handleSearch()}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Search
                </Button>
                <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 2 }}>
                  Reset
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  Query: <code>{buildAdvancedQuery() || '(empty)'}</code>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {results && (
        <Grid container spacing={3}>
          {/* Facets Panel */}
          {Object.keys(facets).length > 0 && (
            <Grid item xs={12} md={3}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 3, position: 'sticky', top: 16 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Filter Results
                  </Typography>
                  {Object.keys(activeFilters).length > 0 && (
                    <Button size="small" onClick={clearAllFilters}>Clear All</Button>
                  )}
                </Box>

                {sortFacetsBooleansFirst(['chapters', 'provision_types', 'amendment_statuses', 'themes'], facets).map((facetName) => (
                  facets[facetName] && facets[facetName].length > 0 && (
                    <Accordion key={facetName} defaultExpanded={['chapters', 'provision_types'].includes(facetName)}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">
                          {facetDisplayName(facetName)}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <List dense sx={{ py: 0, maxHeight: 200, overflow: 'auto' }}>
                          {facets[facetName].slice(0, 15).map((facet) => (
                            <ListItem key={facet.value} disablePadding>
                              <Checkbox
                                size="small"
                                checked={activeFilters[facetToField(facetName)]?.includes(String(facet.value)) || false}
                                onChange={() => handleFacetClick(facetToField(facetName), String(facet.value))}
                              />
                              <ListItemText
                                primary={`${facet.value} (${facet.count.toLocaleString()})`}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )
                ))}
              </Paper>
            </Grid>
          )}

          {/* Results */}
          <Grid item xs={12} md={Object.keys(facets).length > 0 ? 9 : 12}>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="subtitle1">
                    {results.total.toLocaleString()} provisions found
                  </Typography>
                  <PageSizeSelector value={perPage} onChange={handlePageSizeChange} />
                </Box>
                {results.total > perPage && (
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>

              <Divider sx={{ mb: 2 }} />

              <List disablePadding>
                {results.hits.map((hit, idx) => (
                  <React.Fragment key={hit.id}>
                    <ListItem
                      alignItems="flex-start"
                      sx={{
                        flexDirection: 'column',
                        py: 2,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        borderRadius: 1
                      }}
                      onClick={() => toggleDetails(hit.id)}
                    >
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="resultTitle">
                            {hit.section_number ? `Section ${hit.section_number}` : hit.provision_type || 'Provision'}
                            {hit.title && `: ${hit.title}`}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {hit.amendment_status && (
                              <Chip
                                label={hit.amendment_status}
                                size="small"
                                color={getAmendmentStatusColor(hit.amendment_status)}
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                          {hit.chapter && (
                            <Chip label={`Chapter ${hit.chapter}`} size="small" variant="outlined" />
                          )}
                          {hit.part && (
                            <Chip label={`Part ${hit.part}`} size="small" variant="outlined" />
                          )}
                          {hit.provision_type && (
                            <Chip label={hit.provision_type} size="small" variant="outlined" color="primary" />
                          )}
                        </Box>

                        {/* Show highlights or truncated content */}
                        {hit._highlights?.content ? (
                          <Typography
                            className="ps-snippet"
                            variant="body2"
                            color="text.secondary"
                            dangerouslySetInnerHTML={{
                              __html: hit._highlights.content.join('... ')
                            }}
                          />
                        ) : (
                          <Typography className="ps-snippet" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {hit.content?.substring(0, 200)}{hit.content?.length > 200 ? '...' : ''}
                          </Typography>
                        )}

                        {/* Expanded details */}
                        <Collapse in={expandedDetails[hit.id]}>
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                              {hit.content}
                            </Typography>

                            {hit.chapter_title && (
                              <Typography variant="body2" color="text.secondary">
                                <strong>Chapter Title:</strong> {hit.chapter_title}
                              </Typography>
                            )}
                            {hit.part_title && (
                              <Typography variant="body2" color="text.secondary">
                                <strong>Part Title:</strong> {hit.part_title}
                              </Typography>
                            )}
                            {hit.themes && hit.themes.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" color="text.secondary" component="span">
                                  <strong>Themes:</strong>{' '}
                                </Typography>
                                {hit.themes.map((theme, i) => (
                                  <Chip
                                    key={i}
                                    label={theme}
                                    size="small"
                                    sx={{ mr: 0.5, mb: 0.5 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleFacetClick('themes', theme);
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                            {hit.amendment_history && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                <strong>Amendment History:</strong> {JSON.stringify(hit.amendment_history)}
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    </ListItem>
                    {idx < results.hits.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>

              {results.total > perPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
